import React from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Search, Menu, Instagram, ChevronDown } from "lucide-react";
import { NAV, SITE } from "../mock";
import { useCart } from "../context/CartContext";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "./ui/sheet";

export default function Navbar() {
  const { count, setIsOpen } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  return (
    <header className="sticky top-0 z-40 bg-[hsl(var(--kq-bg))] border-b border-[hsl(var(--kq-line))]">
      <div className="max-w-[1400px] mx-auto px-4 md:px-10">
        <div className="flex items-center justify-between py-4 md:py-6 gap-2">
          {/* Left nav */}
          <nav className="hidden md:flex items-center gap-7 text-[11px] tracking-[0.22em] uppercase flex-1">
            {NAV.slice(0, 3).map((n) => (
              <Link key={n.label} to={n.to} className="kq-link">
                {n.label}
              </Link>
            ))}
          </nav>

          {/* Mobile menu */}
          <div className="md:hidden">
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger aria-label="Open menu" className="p-2 -ml-2" data-testid="mobile-menu-trigger">
                <Menu className="w-5 h-5" />
              </SheetTrigger>
              <SheetContent side="left" className="bg-[hsl(var(--kq-bg))] border-r border-[hsl(var(--kq-line))]">
                <SheetTitle className="sr-only">Navigation menu</SheetTitle>
                <SheetDescription className="sr-only">Browse Kaftan Queens — Home, Shop, Our Story, and Instagram.</SheetDescription>
                <div className="mt-10 flex flex-col gap-5">
                  {NAV.map((n) => (
                    <Link
                      key={n.label}
                      to={n.to}
                      className="font-display text-3xl"
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {n.label}
                    </Link>
                  ))}
                  <a
                    href={SITE.instagram}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-6 inline-flex items-center gap-2 text-sm"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Instagram className="w-4 h-4" /> @kaftan.queens
                  </a>
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {/* Centered Logo — larger, with negative vertical margin so bar height stays unchanged */}
          <Link to="/" className="flex items-center justify-center select-none px-1 sm:px-4 min-w-0 overflow-visible" data-testid="navbar-logo-link">
            <img
              src="/brand/logo-600.png"
              alt="Kaftan Queens"
              className="h-16 sm:h-20 md:h-24 w-auto -my-2 sm:-my-3 md:-my-4"
              loading="eager"
              decoding="async"
            />
          </Link>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-7 text-[11px] tracking-[0.22em] uppercase flex-1 justify-end">
            {NAV.slice(3).map((n) => (
              <Link key={n.label} to={n.to} className="kq-link">
                {n.label}
              </Link>
            ))}
          </div>

          {/* Icons */}
          <div className="flex items-center gap-4 md:gap-5 md:ml-6">
            <button
              onClick={() => setIsOpen(true)}
              aria-label="Cart"
              className="relative inline-flex items-center gap-2 hover:text-[hsl(var(--kq-accent-2))] transition-colors"
            >
              <ShoppingBag className="w-[18px] h-[18px]" strokeWidth={1.4} />
              <span className="hidden md:inline text-[11px] tracking-[0.22em] uppercase">Cart ({count})</span>
              {count > 0 && (
                <span className="md:hidden absolute -top-1.5 -right-1.5 bg-[hsl(var(--kq-accent-2))] text-[hsl(var(--kq-bg))] text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
